import React from "react";
import { getQuantityIndex } from "diagnostics-domain/quantity/metrology";
import type { QuantityWithMetrology } from "diagnostics-domain/quantity/metrology";
import { useMetrologyCrossFilters, type MetrologyCrossFilters } from "./useMetrologyCrossFilters";
import type { Setup } from "./useSetups";
import type { Output } from "./call";
import call from "./call";
import useQuery, { useGetQueryData, useMutateBody } from "./useQuery";
import { useChannelUpdates } from "./ws/useChannelUpdates";
import { comparatorBy } from "diagnostics-lib/comparator";
const PATH = "/projects/:projectId(uuid)/quantities" as const;
type Collection = Output<"GET", typeof PATH>["body"]["collection"];
type Dependencies = Output<"GET", typeof PATH>["body"]["dependencies"];
const quantityCache = new WeakMap<
  Collection,
  { collection: Collection; dependencies: Dependencies }
>();
function useQuantitiesAPI(projectId: string): {
  collection: Collection;
  dependencies: Dependencies;
} {
  const { collection, dependencies } = useQuery(PATH, { routeParams: { projectId } }).body;
  const getCachedQuantities = useGetQueryData(PATH, { routeParams: { projectId } });
  const dependenciesCache = React.useRef<Dependencies>();
  const fetchUpdatedItems = React.useCallback(
    async (since: string | null) => {
      const { collection, dependencies, tombstoneIds } = (
        await call("GET", PATH, {
          routeParams: { projectId },
          query: { since: since === null ? new Date(0) : new Date(since) },
        })
      ).body;
      dependenciesCache.current = dependencies;
      return [collection, tombstoneIds] as const;
    },
    [projectId],
  );

  const mutate = useMutateBody(PATH, { routeParams: { projectId } });
  useChannelUpdates(
    { key: "quantities", projectId },
    {
      readItems: React.useCallback(
        () => getCachedQuantities()?.body.collection,
        [getCachedQuantities],
      ),
      fetchUpdatedItems,
      mutateItems: React.useCallback(
        (params: (oldItems: Collection) => Collection) => {
          const dependencies = dependenciesCache.current;
          dependenciesCache.current = undefined;
          if (dependencies === undefined) throw new Error("dependenciesCache.current is undefined");
          return mutate(({ collection, ...rest }) => ({
            ...rest,
            collection: params(collection),
            dependencies,
          }));
        },
        [mutate],
      ),
    },
  );

  return React.useMemo(() => {
    const cached = quantityCache.get(collection);
    if (cached) return cached;
    const result = { dependencies, collection };
    quantityCache.set(collection, result);
    return result;
  }, [collection, dependencies]);
}

export type Quantity = QuantityWithMetrology<
  Omit<Collection[0], "equipments"> & { equipmentIds: string[] } & (
      | { type: "MEASURE"; formula: null; sensor: { id: string; tag: string } }
      | { type: "FORMULA"; sensor: null; formula: string; locked: boolean; isAutoLocked: boolean }
    )
>;
export type QuantityIndex = { [quantityId: string]: Quantity };
const quantityIndexCache = new WeakMap<Collection, WeakMap<MetrologyCrossFilters, QuantityIndex>>();
export function useQuantityIndex(projectId: string): QuantityIndex {
  const { collection, dependencies } = useQuantitiesAPI(projectId);
  const crossFilters = useMetrologyCrossFilters(projectId);
  return React.useMemo(() => {
    const cached = quantityIndexCache.get(collection)?.get(crossFilters);
    if (cached) return cached;
    const quantities = collection.map(({ sensor, formula, equipments, hidden, ...rest }) => {
      if (sensor !== null) {
        return {
          ...rest,
          hidden,
          type: "MEASURE" as const,
          formula: null,
          sensor,
          equipmentIds: equipments.map(e => e.equipmentId),
        };
      } else if (formula === null) {
        throw new Error("quantity must have either a sensor or a formula");
      } else {
        return {
          ...rest,
          hidden,
          type: "FORMULA" as const,
          sensor: null,
          formula,
          equipmentIds: equipments.map(e => e.equipmentId),
        };
      }
    });

    const quantityIndex = getQuantityIndex(quantities, crossFilters, dependencies);
    const crossCache = quantityIndexCache.get(collection);
    if (crossCache) {
      crossCache.set(crossFilters, quantityIndex);
    } else {
      quantityIndexCache.set(collection, new WeakMap([[crossFilters, quantityIndex]]));
    }
    return quantityIndex;
  }, [crossFilters, dependencies, collection]);
}

export type QuantityByName = { [quantityName: string]: Quantity };
const quantityByNameCache = new WeakMap<Quantity[], QuantityByName>();
export function useQuantityByName(projectId: string): QuantityByName {
  const quantities = useQuantities(projectId);
  return React.useMemo(() => {
    const cached = quantityByNameCache.get(quantities);
    if (cached) return cached;
    const quantityByName: QuantityByName = {};
    for (const quantity of quantities) quantityByName[quantity.name] = quantity;
    quantityByNameCache.set(quantities, quantityByName);
    return quantityByName;
  }, [quantities]);
}

const quantitiesCache = new WeakMap<QuantityIndex, Quantity[]>();
export function useQuantities(projectId: string): Quantity[] {
  const quantityIndex = useQuantityIndex(projectId);
  return React.useMemo(() => {
    if (quantitiesCache.has(quantityIndex)) return quantitiesCache.get(quantityIndex)!;
    const result = Object.values(quantityIndex).sort(comparatorBy("createdAt"));

    quantitiesCache.set(quantityIndex, result);
    return result;
  }, [quantityIndex]);
}

export const QUANTITY_ROLES = [
  "simulationInput",
  "simulationOutput",
  "observable",
  "others",
] as const;
export type QuantityRole = (typeof QUANTITY_ROLES)[number];

const setupRolesCache = new WeakMap<
  Setup,
  {
    simulationInputQIds: Set<string>;
    simulationOutputQIds: Set<string>;
    observableQIds: Set<string>;
  }
>();

export function quantityRoles(quantity: { id: string }, setups: Setup[]): Set<QuantityRole> {
  const roles = new Set<QuantityRole>();

  for (const setup of setups) {
    let setupRoles = setupRolesCache.get(setup);
    if (!setupRoles) {
      setupRoles = {
        simulationInputQIds: new Set(setup.simulationInputs.map(si => si.quantityId)),
        simulationOutputQIds: new Set(setup.simulationOutputs.map(so => so.quantityId)),
        observableQIds: new Set(
          setup.diagnosisInputs.filter(di => di.enabled).map(di => di.quantityId),
        ),
      };
      setupRolesCache.set(setup, setupRoles);
    }

    if (setupRoles.observableQIds.has(quantity.id)) roles.add("observable");
    if (setupRoles.simulationInputQIds.has(quantity.id)) roles.add("simulationInput");
    if (setupRoles.simulationOutputQIds.has(quantity.id)) roles.add("simulationOutput");
  }
  if (roles.size === 0) roles.add("others");
  return roles;
}

export type Observable = Quantity & { enabled: boolean };
